import React, { useState } from 'react';
import { 
  Box, TextField, Button, Typography, 
  InputAdornment, IconButton, Paper, 
  CircularProgress, Alert, Container, 
  Fade, GlobalStyles 
} from '@mui/material';
import { 
  MailOutline, Lock, Visibility, VisibilityOff, ArrowForward 
} from '@mui/icons-material';

// CORRECT IMPORT FOR IMAGES
import logoImg from "../assets/logo.png";

export const Login = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/console-users/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Access Denied');

      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
      }
      onLogin(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      height: '100vh', 
      width: '100vw', 
      background: 'linear-gradient(135deg, #2F727C 0%, #2FC0CC 100%)',
      alignItems: 'center', 
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* 1. BACKGROUND ANIMATION & PATTERN */}
      <GlobalStyles styles={{
        '@keyframes moveGrid': {
          '0%': { backgroundPosition: '0 0' },
          '100%': { backgroundPosition: '40px 40px' }
        }
      }} />
      <Box sx={{ 
        position: 'absolute', width: '100%', height: '100%', 
        opacity: 0.1,
        backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
        animation: 'moveGrid 3s linear infinite',
        zIndex: 0
      }} />

      <Container maxWidth="xs" sx={{ z_index: 1 }}>
        <Fade in={true} timeout={1000}>
          <Paper elevation={0} sx={{ 
            p: 4, // Reduced padding for a cleaner look
            borderRadius: 4, 
            bgcolor: 'rgba(255, 255, 255, 0.96)', 
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            textAlign: 'center',
            backdropFilter: 'blur(10px)'
          }}>
            
            {/* 2. LOGO AREA */}
            <Box sx={{ height: 45, mb: 3, display: 'flex', justifyContent: 'center' }}>
                <img 
                    src={logoImg} 
                    alt="FleetGO Logo" 
                    style={{ height: '100%', width: 'auto', objectFit: 'contain' }} 
                    onError={(e) => console.error("Logo failed to load from assets", e)}
                /> 
            </Box>

   <Box sx={{ mb: 4 }}>
  <Typography variant="caption" sx={{ color: '#2FC0CC', fontWeight: 900, letterSpacing: 2, display: 'block', mb: -0.5 }}>
    TMS
  </Typography>
  <Typography variant="h5" sx={{ color: '#2F727C', fontWeight: 900 }}>
    CENTRAL CONSOLE
  </Typography>
</Box>

            <form onSubmit={handleLogin}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                
                {error && (
                  <Alert severity="error" sx={{ borderRadius: 2, fontSize: '0.75rem', mb: 1 }}>
                    {error}
                  </Alert>
                )}

                {/* 3. REFINED "SOFT" INPUTS */}
                <TextField
                  name="email"
                  placeholder="Email Address"
                  variant="outlined"
                  fullWidth
                  size="small" // Makes it more modern/less bulky
                  value={credentials.email}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutline fontSize="small" sx={{ color: '#2FC0CC' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: 2,
                      bgcolor: '#f1f5f7',
                      '& fieldset': { border: 'none' }, // Ghost style
                      '&:hover': { bgcolor: '#e8ecef' },
                      '&.Mui-focused': { bgcolor: '#fff', boxShadow: '0 0 0 2px #2FC0CC33' }
                    } 
                  }}
                />

                <TextField
                  name="password"
                  placeholder="Password"
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={credentials.password}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock fontSize="small" sx={{ color: '#2FC0CC' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} size="small" edge="end">
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: 2,
                      bgcolor: '#f1f5f7',
                      '& fieldset': { border: 'none' },
                      '&:hover': { bgcolor: '#e8ecef' },
                      '&.Mui-focused': { bgcolor: '#fff', boxShadow: '0 0 0 2px #2FC0CC33' }
                    } 
                  }}
                />

                {/* 4. MODERN ACTION BUTTON */}
                <Button 
                  type="submit"
                  variant="contained" 
                  disabled={loading}
                  fullWidth
                  sx={{ 
                    py: 1.5, 
                    mt: 1,
                    fontSize: '0.85rem',
                    fontWeight: 800,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #fcc433 0%, #f6b93b 100%)',
                    color: '#1A3A3F',
                    boxShadow: '0 4px 12px rgba(246, 185, 59, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #fbbd1e 0%, #f6b93b 100%)',
                      boxShadow: '0 6px 15px rgba(246, 185, 59, 0.4)',
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: '#1A3A3F' }} />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Login <ArrowForward fontSize="small" />
                    </Box>
                  )}
                </Button>
              </Box>
            </form>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};